import { useState, useEffect, useCallback } from 'react';
import { Organization, Team, Member, Subscription } from '../types';
import { ApiError } from '../utils/error-handler';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { getErrorMessageString, logError, extractErrorMessage } from '../utils/error-handler';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';

interface OrganizationData {
  organization?: Organization;
  userRole?: string;
  teams: Team[];
  members: Member[];
  permissions: string[];
  subscription?: Subscription;
}

interface LoadingStates {
  organization: boolean;
  teams: boolean;
  members: boolean;
  subscription: boolean;
}

interface ErrorStates {
  organization?: ApiError;
  teams?: ApiError;
  members?: ApiError;
  subscription?: ApiError;
}

export function useOrganizationOverview(organizationId: string | undefined) {
  const { user } = useAuth();
  const { getOrganizationSubscriptions, refreshData } = useOrganization(); // Get methods from context
  const [data, setData] = useState<OrganizationData>({
    teams: [],
    members: [],
    permissions: []
  });
  const [loading, setLoading] = useState<LoadingStates>({
    organization: true,
    teams: true,
    members: true,
    subscription: true
  });
  const [errors, setErrors] = useState<ErrorStates>({});

  const updateLoading = useCallback((section: keyof LoadingStates, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [section]: isLoading }));
  }, []);

  const updateError = useCallback((section: keyof ErrorStates, error?: string) => {
    setErrors(prev => ({ ...prev, [section]: error ? { message: error } : undefined }));
  }, []);

  const fetchOrganizationData = useCallback(async () => {
    if (!organizationId) {
      setData({ teams: [], members: [], permissions: [] });
      setLoading({ organization: false, teams: false, members: false, subscription: false });
      setErrors({});
      return;
    }

    updateLoading('organization', true);
    updateError('organization', undefined);

    try {
      console.log(`Fetching organization details for: ${organizationId}`);
      const response = await organizationService.getOrganization(organizationId);
      
      // API v2 returns { data: { organization: {...} }, metadata: {...}, message: "..." }
      // response.data is the outer data wrapper, organization is nested inside
      const organization = (response.data as any).organization || response.data;
      
      // Map API v2 fields to our interface (handles createdOn -> createdAt)
      const orgData = {
        ...organization,
        // Ensure both id and _id are set for compatibility between interfaces
        id: organization.id || organization._id || '',
        _id: organization.id || organization._id || '',
        // Map createdOn/updatedOn from API v2 to createdAt/updatedAt
        createdAt: organization.createdOn || organization.createdAt,
        updatedAt: organization.updatedOn || organization.updatedAt,
      } as Organization;
      
      setData(prev => ({
        ...prev,
        organization: orgData,
        // Default permissions, should be fetched from RBAC service  
        permissions: [] // Will be updated when members are fetched
      }));
      
      updateError('organization', undefined);
    } catch (err) {
      logError(`Error fetching organization details for ${organizationId}`, err);
      updateError('organization', getErrorMessageString(err));
    } finally {
      updateLoading('organization', false);
    }
  }, [organizationId, updateLoading, updateError]);

  const fetchTeams = useCallback(async () => {
    if (!organizationId) return;

    updateLoading('teams', true);
    updateError('teams', undefined);

    try {
      const teams = await organizationService.listOrganizationTeams(organizationId);
      setData(prev => ({
        ...prev,
        teams: teams.map(team => ({
          ...team,
          id: team._id || (team as any).id || '',
          _id: team._id || (team as any).id || '',
          organizationName: prev.organization?.name || ''
        } as Team))
      }));
      updateError('teams', undefined);
    } catch (err) {
      logError(`Error fetching teams for ${organizationId}`, err);
      updateError('teams', getErrorMessageString(err));
    } finally {
      updateLoading('teams', false);
    }
  }, [organizationId, updateLoading, updateError]);

  const fetchMembers = useCallback(async () => {
    if (!organizationId) return;

    updateLoading('members', true);
    updateError('members', undefined);

    try {
      const rawMembers = await organizationService.listOrganizationMembers(organizationId);
      
      // Process members - organizationService now properly maps the data
      const processedMembers = (rawMembers as any[]).map(member => {
        const memberId = member.id || member._id;
        
        // Check for user data in nested object first (from service mapping)
        // then fall back to direct fields
        const userId = member.user_id || member.userId;
        const name = member.name || member.user?.name;
        const email = member.email || member.user?.email;
        const roleName = member.role_name || member.roleName || member.role;
        const status = member.status;
        const joinedAt = member.joined_at || member.joinedAt || member.createdAt;
        const updatedAt = member.updated_at || member.updatedAt || member.updatedAt;
        
        return {
          id: memberId,
          userId: userId,
          name: name || 'Unknown',
          email: email || 'Unknown',
          role: roleName || 'Unknown Role',
          status: status || 'unknown',
          createdAt: joinedAt || new Date().toISOString(),
          updatedAt: updatedAt || new Date().toISOString(),
        } as Member;
      });
      
      // Get current user's role from their memberships data instead of organization members list
      let currentUserRole = 'Member'; // Default fallback
      if (user) {
        try {
          const { memberships } = await userService.getUserMemberships();
          const orgMembership = memberships.find((membership: any) => 
            membership.context?.type === 'ORGANIZATION' && 
            membership.context?.entity_id === organizationId
          );
          
          if (orgMembership) {
            currentUserRole = (orgMembership as any).role_name || 'Member';
            console.log(`Found user role for organization ${organizationId}:`, currentUserRole);
          } else {
            console.log(`No organization membership found for user in organization ${organizationId}`);
          }
        } catch (membershipError) {
          logError('Error fetching user memberships for role determination', membershipError);
          // Fall back to the old method if memberships API fails
          const currentUserMember = processedMembers.find(member => 
            member.userId === user._id || 
            member.email === user.email ||
            member.id === user._id
          );
          
          if (currentUserMember) {
            currentUserRole = currentUserMember.role || 'Member';
          }
        }
      }
      
      setData(prev => ({ 
        ...prev, 
        members: processedMembers,
        userRole: currentUserRole
      }));
      updateError('members', undefined);
    } catch (err) {
      logError(`Error fetching members for ${organizationId}`, err);
      updateError('members', getErrorMessageString(err));
    } finally {
      updateLoading('members', false);
    }
  }, [organizationId, user, updateLoading, updateError]);

  const fetchSubscription = useCallback(() => {
    if (!organizationId) return;

    updateLoading('subscription', true);
    updateError('subscription', undefined);

    try {
      // Use cached subscription data from OrganizationContext
      const subscriptions = getOrganizationSubscriptions(organizationId);
      // Get the first/default subscription
      const subscription = subscriptions.length > 0 ? subscriptions[0] : undefined;
      setData(prev => ({ ...prev, subscription }));
      updateError('subscription', undefined);
    } catch (err) {
      logError(`Error getting subscription for ${organizationId}`, err);
      updateError('subscription', getErrorMessageString(err));
      // Don't treat subscription errors as critical - organization might not have subscription
    } finally {
      updateLoading('subscription', false);
    }
  }, [organizationId, getOrganizationSubscriptions, updateLoading, updateError]);

  const fetchOverview = useCallback(async () => {
    // Fetch all data concurrently but handle errors independently
    await Promise.allSettled([
      fetchOrganizationData(),
      fetchTeams(),
      fetchMembers(),
      fetchSubscription()
    ]);
  }, [fetchOrganizationData, fetchTeams, fetchMembers, fetchSubscription]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const updateOrganization = async (updateData: { name?: string; description?: string }) => {
    if (!organizationId || !data.organization) {
      return {
        success: false,
        error: { code: 'INVALID_REQUEST', message: 'No organization loaded', status: 400 }
      };
    }

    try {
      updateLoading('organization', true);
      const updated = await organizationService.updateOrganization(organizationId, updateData);

      // Refresh global context data (updates organization name everywhere, including sidebar)
      await refreshData();

      // Update the local organization data
      setData(prev => ({
        ...prev,
        organization: prev.organization ? {
          ...prev.organization,
          ...updated,
          createdAt: typeof updated.createdAt === 'string' ? updated.createdAt : updated.createdAt?.toISOString() || prev.organization.createdAt,
          updatedAt: typeof updated.updatedAt === 'string' ? updated.updatedAt : updated.updatedAt?.toISOString() || new Date().toISOString()
        } : undefined
      }));

      return { success: true };
    } catch (err) {
      const error = { message: getErrorMessageString(err) };
      return { success: false, error };
    } finally {
      updateLoading('organization', false);
    }
  };

  const deleteOrganization = async () => {
    if (!organizationId) {
      return {
        success: false,
        error: { code: 'INVALID_REQUEST', message: 'No organization ID', status: 400 }
      };
    }

    try {
      updateLoading('organization', true);
      await organizationService.deleteOrganization(organizationId);

      // Clear the deleted organization from local state immediately
      // This prevents showing stale data before navigation
      setData(prev => ({
        ...prev,
        organization: undefined
      }));

      // Refresh the global organization list in OrganizationContext
      // This ensures the organization is removed from the dashboard list
      await refreshData();

      return { success: true };
    } catch (err) {
      logError('Failed to delete organization', err);
      const errorDetails = extractErrorMessage(err);
      return {
        success: false,
        error: {
          message: errorDetails.message,
          code: errorDetails.code,
          status: errorDetails.status
        }
      };
    } finally {
      updateLoading('organization', false);
    }
  };

  // Calculate overall loading state - only show loading if organization is loading
  const isLoading = loading.organization;
  
  // Calculate overall error - only show error if organization failed and we have no org data
  const error = errors.organization && !data.organization ? errors.organization : null;

  return {
    // Individual data sections
    organization: data.organization || null,
    userRole: data.userRole,
    teams: data.teams,
    members: data.members,
    permissions: data.permissions,
    subscription: data.subscription,
    
    // Loading states
    isLoading,
    loading,
    
    // Error states
    error,
    errors,
    
    // Helper functions
    refreshOverview: fetchOverview,
    updateOrganization,
    deleteOrganization,
    
    // Individual refresh functions for granular control
    refreshOrganization: fetchOrganizationData,
    refreshTeams: fetchTeams,
    refreshMembers: fetchMembers,
    refreshSubscription: fetchSubscription
  };
}

export default useOrganizationOverview;